// components/ui/sidebar.jsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

// Constants
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";
const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 1 week

// Context
const SidebarContext = createContext(null);

// Hook
export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

// Provider
export const SidebarProvider = ({
  children,
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  style,
  ...props
}) => {
  const [open, _setOpen] = useState(defaultOpen);
  const [openMobile, setOpenMobile] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 768);
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key === SIDEBAR_KEYBOARD_SHORTCUT
      ) {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const setOpen = (value) => {
    const openState = typeof value === "function" ? value(open) : value;
    if (setOpenProp) {
      setOpenProp(openState);
    } else {
      _setOpen(openState);
    }
    document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setOpenMobile((prev) => !prev);
    } else {
      setOpen((prev) => !prev);
    }
  };

  const state = open ? "expanded" : "collapsed";

  return (
    <SidebarContext.Provider
      value={{
        state,
        open,
        setOpen,
        openMobile,
        setOpenMobile,
        isMobile,
        toggleSidebar,
      }}
    >
      <div
        className="group/sidebar-wrapper"
        style={
          {
            "--sidebar-width": SIDEBAR_WIDTH,
            "--sidebar-width-mobile": SIDEBAR_WIDTH_MOBILE,
            ...style,
          }
        }
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
};

// Main Sidebar Component
const sidebarVariants = {
  side: {
    left: "left-0",
    right: "right-0",
  },
  variant: {
    sidebar: "bg-sidebar border-r border-sidebar-border",
    floating: "bg-background shadow-md",
    inset: "bg-sidebar",
  },
  collapsible: {
    offcanvas: "md:data-[state=collapsed]:-translate-x-full md:data-[state=collapsed]:absolute",
    icon: "md:data-[state=collapsed]:w-[calc(var(--sidebar-width)*0.2)]",
    none: "",
  },
};

const sidebarStyle = cva(
  "fixed top-0 z-40 h-screen transition-[width,transform] duration-200 ease-linear",
  {
    variants: sidebarVariants,
    defaultVariants: {
      side: "left",
      variant: "sidebar",
      collapsible: "offcanvas",
    },
  }
);

export const Sidebar = ({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}) => {
  const { state, isMobile, openMobile } = useSidebar();
  const isOffcanvas = collapsible === "offcanvas";
  const isIcon = collapsible === "icon";

  if (isMobile) {
    return (
      <div
        data-state={openMobile ? "expanded" : "collapsed"}
        data-mobile="true"
        className={cn(
          "fixed inset-0 z-50 bg-background/80 backdrop-blur-sm data-[state=collapsed]:hidden",
          className
        )}
        {...props}
      >
        <div
          className={cn(
            "h-full w-[--sidebar-width-mobile] bg-sidebar border-r border-sidebar-border transition-transform duration-200 ease-linear",
            side === "left" ? "left-0" : "right-0",
            openMobile ? "translate-x-0" : side === "left" ? "-translate-x-full" : "translate-x-full"
          )}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      data-state={state}
      data-side={side}
      data-variant={variant}
      data-collapsible={collapsible}
      className={cn(
        sidebarStyle({ side, variant, collapsible }),
        isOffcanvas && "md:data-[state=collapsed]:-translate-x-full",
        isIcon && "md:data-[state=collapsed]:w-[calc(var(--sidebar-width)*0.2)]",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

// Sidebar Header Component
export const SidebarHeader = ({ className, children, ...props }) => {
  return (
    <div
      className={cn("flex flex-col gap-2 p-2 border-b border-sidebar-border", className)}
      {...props}
    >
      {children}
    </div>
  );
};

// Sidebar Footer Component
export const SidebarFooter = ({ className, children, ...props }) => {
  return (
    <div
      className={cn("flex flex-col gap-2 p-2 border-t border-sidebar-border", className)}
      {...props}
    >
      {children}
    </div>
  );
};

// Sidebar Content Component
export const SidebarContent = ({ className, children, ...props }) => {
  return (
    <div className={cn("flex-1 overflow-auto", className)} {...props}>
      {children}
    </div>
  );
};

// Sidebar Group Component
export const SidebarGroup = ({ className, children, ...props }) => {
  return (
    <div className={cn("p-0", className)} {...props}>
      {children}
    </div>
  );
};

// Sidebar Group Label Component
export const SidebarGroupLabel = ({ className, children, asChild = false, ...props }) => {
  const Comp = asChild ? "button" : "div";
  return (
    <Comp
      className={cn(
        "text-xs font-medium text-sidebar-foreground/70 px-2 py-1 uppercase tracking-wide",
        asChild && "w-full text-start hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md transition-colors",
        className
      )}
      {...props}
    >
      {children}
    </Comp>
  );
};

// Sidebar Group Content Component
export const SidebarGroupContent = ({ className, children, ...props }) => {
  return (
    <div className={cn("mt-1", className)} {...props}>
      {children}
    </div>
  );
};

// Sidebar Group Action Component
export const SidebarGroupAction = ({ className, children, ...props }) => {
  return (
    <button
      className={cn(
        "absolute right-1 top-1.5 h-6 w-6 rounded-md text-sidebar-foreground/70 hover:text-sidebar-foreground flex items-center justify-center",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
};

// Sidebar Menu Component
export const SidebarMenu = ({ className, children, ...props }) => {
  return (
    <ul className={cn("flex flex-col gap-1", className)} {...props}>
      {children}
    </ul>
  );
};

// Sidebar Menu Item Component
export const SidebarMenuItem = ({ className, children, ...props }) => {
  return (
    <li className={cn("group/menu-item relative", className)} {...props}>
      {children}
    </li>
  );
};

// Sidebar Menu Button Component
export const SidebarMenuButton = ({ 
  className, 
  children, 
  isActive = false, 
  asChild = false, 
  ...props 
}) => {
  const Comp = asChild ? "a" : "button";
  
  return (
    <Comp
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm transition-colors",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        "data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground",
        isActive && "bg-sidebar-primary text-sidebar-primary-foreground",
        className
      )}
      data-active={isActive}
      {...props}
    >
      {children}
    </Comp>
  );
};

// Sidebar Menu Action Component
export const SidebarMenuAction = ({ className, children, ...props }) => {
  return (
    <button
      className={cn(
        "absolute right-1 top-1/2 -translate-y-1/2 rounded-md p-1 text-sidebar-foreground/70 opacity-0 transition-opacity",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        "group-hover/menu-item:opacity-100 group-focus-within/menu-item:opacity-100",
        "focus:opacity-100 focus:outline-none",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
};

// Sidebar Menu Sub Component
export const SidebarMenuSub = ({ className, children, ...props }) => {
  return (
    <ul className={cn("ml-4 mt-1 flex flex-col gap-1 border-l border-sidebar-border pl-2", className)} {...props}>
      {children}
    </ul>
  );
};

// Sidebar Menu Sub Item Component
export const SidebarMenuSubItem = ({ className, children, ...props }) => {
  return (
    <li className={cn("group/menu-sub-item relative", className)} {...props}>
      {children}
    </li>
  );
};

// Sidebar Menu Sub Button Component
export const SidebarMenuSubButton = ({ className, children, isActive = false, asChild = false, ...props }) => {
  const Comp = asChild ? "a" : "button";
  
  return (
    <Comp
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm transition-colors",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        "data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground",
        isActive && "bg-sidebar-primary text-sidebar-primary-foreground",
        className
      )}
      data-active={isActive}
      {...props}
    >
      {children}
    </Comp>
  );
};

// Sidebar Menu Badge Component
export const SidebarMenuBadge = ({ className, children, ...props }) => {
  return (
    <div
      className={cn(
        "absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-sidebar-primary px-1.5 py-0.5 text-xs text-sidebar-primary-foreground",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

// Sidebar Menu Skeleton Component
export const SidebarMenuSkeleton = ({ className, showIcon = false, ...props }) => {
  return (
    <div
      className={cn("flex w-full items-center gap-2 rounded-md px-2 py-1.5", className)}
      {...props}
    >
      {showIcon && <div className="h-4 w-4 rounded-md bg-sidebar-accent animate-pulse" />}
      <div className="h-4 flex-1 rounded-md bg-sidebar-accent animate-pulse" />
    </div>
  );
};

// Sidebar Separator Component
export const SidebarSeparator = ({ className, ...props }) => {
  return (
    <hr
      className={cn("my-2 border-sidebar-border", className)}
      {...props}
    />
  );
};

// Sidebar Trigger Component
export const SidebarTrigger = ({ className, children, ...props }) => {
  const { toggleSidebar } = useSidebar();
  
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors",
        "hover:bg-accent hover:text-accent-foreground",
        "h-9 w-9 shrink-0 md:h-7 md:w-7",
        className
      )}
      onClick={toggleSidebar}
      {...props}
    >
      {children || (
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.5 3C1.22386 3 1 3.22386 1 3.5C1 3.77614 1.22386 4 1.5 4H13.5C13.7761 4 14 3.77614 14 3.5C14 3.22386 13.7761 3 13.5 3H1.5ZM1 7.5C1 7.22386 1.22386 7 1.5 7H13.5C13.7761 7 14 7.22386 14 7.5C14 7.77614 13.7761 8 13.5 8H1.5C1.22386 8 1 7.77614 1 7.5ZM1 11.5C1 11.2239 1.22386 11 1.5 11H13.5C13.7761 11 14 11.2239 14 11.5C14 11.7761 13.7761 12 13.5 12H1.5C1.22386 12 1 11.7761 1 11.5Z"
            fill="currentColor"
            fillRule="evenodd"
            clipRule="evenodd"
          ></path>
        </svg>
      )}
    </button>
  );
};

// Sidebar Rail Component
export const SidebarRail = ({ className, ...props }) => {
  const { toggleSidebar } = useSidebar();
  
  return (
    <div
      className={cn(
        "absolute inset-y-0 right-0 hidden w-2.5 cursor-ew-resize transition-colors md:flex",
        "hover:bg-sidebar-ring/20",
        "after:absolute after:inset-y-0 after:left-1/2 after:w-0.5 after:-translate-x-1/2",
        "after:bg-sidebar-border after:transition-all after:hover:bg-sidebar-ring",
        className
      )}
      onClick={toggleSidebar}
      {...props}
    />
  );
};

// Sidebar Inset Component
export const SidebarInset = ({ className, children, ...props }) => {
  const { state } = useSidebar();
  
  return (
    <div
      className={cn(
        "flex-1 transition-[margin] duration-200 ease-linear",
        "md:ml-[--sidebar-width]",
        "md:data-[state=collapsed]:ml-0",
        state === "collapsed" && "md:ml-0"
      )}
      data-state={state}
      {...props}
    >
      {children}
    </div>
  );
};

